import { useEffect, useState, useImperativeHandle, forwardRef, useCallback, useMemo } from "react";
import { Plus, Play, Trash, CheckCircle, XCircle, MagicWand, Eye, PencilSimple } from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
    listarRegras, criarRegra, atualizarRegra, removerRegra, executarRegra,
    listarVariaveisDisponiveis, previewTemplateRegra,
} from "../lib/api";

const REGRA_VAZIA = {
    nome: "",
    metodo: "POST",
    url_destino: "",
    headers_text: "{\n  \"Content-Type\": \"application/json\"\n}",
    corpo_template: "{\n  \"cookies\": {{cookies_json}},\n  \"url\": \"{{url|json}}\"\n}",
    gatilho_tipo: "manual",
    gatilho_url_contem: "",
    gatilho_url_padrao: "contem",
    gatilho_metodo: "",
    gatilho_tipo_recurso: "",
    gatilho_status_min: 0,
    gatilho_status_max: 0,
    gatilho_intervalo_ms: 60000,
    ativa: true,
    _requisicao_exemplo_id: "", // usado apenas no front pra preview
};

const AbaRegras = forwardRef(function AbaRegras({ botId, eventosRegra }, ref) {
    const [regras, setRegras] = useState([]);
    const [dialogAberto, setDialogAberto] = useState(false);
    const [form, setForm] = useState(REGRA_VAZIA);
    const [salvando, setSalvando] = useState(false);
    const [catalogoVars, setCatalogoVars] = useState({ grupos: [], padroes_url: [] });
    const [campoAtivo, setCampoAtivo] = useState("corpo_template"); // qual textarea recebe a variável quando clica num chip
    const [preview, setPreview] = useState("");
    const [carregandoPreview, setCarregandoPreview] = useState(false);
    const [requisicaoExemplo, setRequisicaoExemplo] = useState(null); // requisição usada como base do preview
    const [editandoId, setEditandoId] = useState(null); // ID da regra sendo editada (null = nova)

    const carregar = useCallback(async () => {
        try {
            const lista = await listarRegras(botId);
            setRegras(lista);
        } catch {
            toast.error("Falha ao listar regras");
        }
    }, [botId]);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        listarVariaveisDisponiveis().then(setCatalogoVars).catch(() => {});
    }, []);

    // API pública: AbaRequisicoes chama isso para abrir o editor já preenchido.
    useImperativeHandle(ref, () => ({
        abrirComRequisicao: (req) => {
            // Pré-preenche o form com dados da requisição capturada
            const headers = JSON.stringify(req.headers_request || {}, null, 2);
            // URL alvo: tenta usar a mesma URL como destino, mas o usuário pode mudar
            const novo = {
                ...REGRA_VAZIA,
                nome: `Encaminhar ${req.metodo} ${(req.url || "").substring(0, 50)}`,
                metodo: "POST", // método de envio (não o capturado)
                url_destino: "",
                headers_text: "{\n  \"Content-Type\": \"application/json\"\n}",
                corpo_template: JSON.stringify({
                    bot_id: "{{bot.id}}",
                    bot_nome: "{{bot.nome|json}}",
                    capturado_em: "{{timestamp}}",
                    metodo: "{{metodo}}",
                    url: "{{url|json}}",
                    status: "{{status}}",
                    // Campos abaixo são JSON literais — NÃO ficam entre aspas
                    headers_request: "__HRJ__",
                    headers_response: "__HSJ__",
                    request_body: "{{request_body|json}}",
                    response_body: "{{response_body|json}}",
                    cookies: "__CJ__",
                }, null, 2)
                    // Substitui marcadores por JSON literal (sem aspas)
                    .replace('"__HRJ__"', "{{headers_request_json}}")
                    .replace('"__HSJ__"', "{{headers_response_json}}")
                    .replace('"__CJ__"', "{{cookies_json}}"),
                gatilho_tipo: "captura",
                gatilho_url_contem: extrairPadraoURL(req.url),
                gatilho_url_padrao: "wildcard",
                gatilho_metodo: req.metodo,
                gatilho_tipo_recurso: req.tipo_recurso || "",
                gatilho_status_min: req.status_codigo >= 200 ? 200 : 0,
                gatilho_status_max: req.status_codigo >= 200 ? 299 : 0,
                _requisicao_exemplo_id: req.id,
            };
            setForm(novo);
            setRequisicaoExemplo(req);
            setDialogAberto(true);
            // Trigger preview
            atualizarPreview(novo.corpo_template, req.id);
        }
    }), [botId]);

    const atualizarPreview = useCallback(async (template, requisicaoID) => {
        if (!template || !requisicaoID) { setPreview(""); return; }
        setCarregandoPreview(true);
        try {
            const r = await previewTemplateRegra({
                template,
                requisicao_id: requisicaoID,
                bot_id: botId,
            });
            setPreview(r.preview || "");
        } catch {
            setPreview("(erro ao gerar preview)");
        } finally {
            setCarregandoPreview(false);
        }
    }, [botId]);

    const salvar = async (e) => {
        e.preventDefault();
        let headers = {};
        try {
            headers = JSON.parse(form.headers_text || "{}");
        } catch {
            toast.error("Headers em JSON inválido");
            return;
        }
        const payload = {
            nome: form.nome,
            metodo: form.metodo,
            url_destino: form.url_destino,
            headers,
            corpo_template: form.corpo_template,
            gatilho_tipo: form.gatilho_tipo,
            gatilho_url_contem: form.gatilho_url_contem,
            gatilho_url_padrao: form.gatilho_url_padrao || "contem",
            gatilho_metodo: form.gatilho_metodo,
            gatilho_tipo_recurso: form.gatilho_tipo_recurso || "",
            gatilho_status_min: Number(form.gatilho_status_min) || 0,
            gatilho_status_max: Number(form.gatilho_status_max) || 0,
            gatilho_intervalo_ms: Number(form.gatilho_intervalo_ms) || 0,
            ativa: form.ativa,
        };
        setSalvando(true);
        try {
            if (editandoId) {
                await atualizarRegra(editandoId, payload);
                toast.success("Regra atualizada");
            } else {
                await criarRegra(botId, payload);
                toast.success("Regra criada");
            }
            setDialogAberto(false);
            setForm(REGRA_VAZIA);
            setRequisicaoExemplo(null);
            setPreview("");
            setEditandoId(null);
            carregar();
        } catch (e) {
            toast.error(e?.response?.data?.erro || "Falha ao salvar");
        } finally {
            setSalvando(false);
        }
    };

    const abrirParaEditar = (regra) => {
        setEditandoId(regra.id);
        setRequisicaoExemplo(null);
        setForm({
            nome: regra.nome || "",
            metodo: regra.metodo || "POST",
            url_destino: regra.url_destino || "",
            headers_text: JSON.stringify(regra.headers || {}, null, 2),
            corpo_template: regra.corpo_template || "",
            gatilho_tipo: regra.gatilho_tipo || "manual",
            gatilho_url_contem: regra.gatilho_url_contem || "",
            gatilho_url_padrao: regra.gatilho_url_padrao || "contem",
            gatilho_metodo: regra.gatilho_metodo || "",
            gatilho_tipo_recurso: regra.gatilho_tipo_recurso || "",
            gatilho_status_min: regra.gatilho_status_min || 0,
            gatilho_status_max: regra.gatilho_status_max || 0,
            gatilho_intervalo_ms: regra.gatilho_intervalo_ms || 60000,
            ativa: regra.ativa !== false,
        });
        setPreview("");
        setDialogAberto(true);
    };

    const alternarAtiva = async (regra) => {
        try {
            await atualizarRegra(regra.id, { ativa: !regra.ativa });
            carregar();
        } catch { toast.error("Falha"); }
    };

    const excluir = async (regra) => {
        if (!window.confirm(`Excluir regra "${regra.nome}"?`)) return;
        try { await removerRegra(regra.id); carregar(); }
        catch { toast.error("Falha"); }
    };

    const executar = async (regra) => {
        try {
            await executarRegra(regra.id);
            toast.success("Execução agendada");
        } catch { toast.error("Falha"); }
    };

    const inserirVariavel = (chave) => {
        // Insere {{chave}} no campo ativo (corpo, headers, url ou nome)
        setForm((f) => {
            const novo = { ...f };
            if (campoAtivo === "corpo_template") novo.corpo_template = (f.corpo_template || "") + chave;
            else if (campoAtivo === "headers_text") novo.headers_text = (f.headers_text || "") + chave;
            else if (campoAtivo === "url_destino") novo.url_destino = (f.url_destino || "") + chave;
            return novo;
        });
    };

    const grupos = catalogoVars.grupos || [];
    const padroes = catalogoVars.padroes_url || [
        { valor: "contem", rotulo: "Contém (substring)" },
        { valor: "wildcard", rotulo: "Wildcard (* e ?)" },
        { valor: "regex", rotulo: "Expressão regular" },
        { valor: "igual", rotulo: "Igual exatamente" },
    ];

    // Quando o usuário muda o template, atualiza preview com debounce (300ms)
    useEffect(() => {
        if (!requisicaoExemplo) return;
        const t = setTimeout(() => {
            atualizarPreview(form.corpo_template, requisicaoExemplo.id);
        }, 300);
        return () => clearTimeout(t);
    }, [form.corpo_template, requisicaoExemplo, atualizarPreview]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-end flex-wrap gap-2">
                <Dialog open={dialogAberto} onOpenChange={(o) => {
                    setDialogAberto(o);
                    if (!o) { setForm(REGRA_VAZIA); setRequisicaoExemplo(null); setPreview(""); setEditandoId(null); }
                }}>
                    <DialogTrigger asChild>
                        <Button size="sm" data-testid="btn-nova-regra"
                            className="bg-white text-zinc-950 hover:bg-zinc-200">
                            <Plus size={14} className="mr-1" /> Nova Regra
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-zinc-950 border border-zinc-800 text-zinc-100 max-w-5xl max-h-[92vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>{editandoId ? "Editar Regra" : (requisicaoExemplo ? "Nova regra (a partir de requisição)" : "Nova Regra de Encaminhamento")}</DialogTitle>
                            <DialogDescription className="text-zinc-400 text-xs">
                                Defina destino HTTP, gatilhos e como os dados capturados serão encaminhados.
                                {requisicaoExemplo && (
                                    <span className="block mt-1 text-emerald-400/80">
                                        Base: <span className="font-mono">{requisicaoExemplo.metodo} {requisicaoExemplo.url}</span>
                                    </span>
                                )}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
                            {/* COLUNA ESQUERDA: FORMULÁRIO */}
                            <form onSubmit={salvar} className="space-y-3" id="form-regra">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    <div className="sm:col-span-2 space-y-1.5">
                                        <Label className="text-xs uppercase tracking-wide text-zinc-400">Nome</Label>
                                        <Input
                                            data-testid="input-nome-regra"
                                            value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })}
                                            className="bg-zinc-900 border-zinc-800" required />
                                    </div>
                                    <div className="space-y-1.5">
                                        <Label className="text-xs uppercase tracking-wide text-zinc-400">Método de envio</Label>
                                        <Select value={form.metodo} onValueChange={(v) => setForm({ ...form, metodo: v })}>
                                            <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="select-metodo-regra">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-zinc-900 border-zinc-800">
                                                {["GET", "POST", "PUT", "PATCH", "DELETE"].map((m) => (
                                                    <SelectItem key={m} value={m}>{m}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wide text-zinc-400">URL de Destino</Label>
                                    <Input
                                        data-testid="input-url-regra"
                                        placeholder="https://api.externa.com/endpoint"
                                        value={form.url_destino}
                                        onChange={(e) => setForm({ ...form, url_destino: e.target.value })}
                                        onFocus={() => setCampoAtivo("url_destino")}
                                        className="bg-zinc-900 border-zinc-800 font-mono text-xs" required />
                                </div>
                                <div className="space-y-1.5">
                                    <Label className="text-xs uppercase tracking-wide text-zinc-400">Headers (JSON)</Label>
                                    <Textarea
                                        data-testid="input-headers-regra"
                                        rows={4}
                                        value={form.headers_text}
                                        onChange={(e) => setForm({ ...form, headers_text: e.target.value })}
                                        onFocus={() => setCampoAtivo("headers_text")}
                                        className="bg-zinc-900 border-zinc-800 font-mono text-xs" />
                                </div>
                                <div className="space-y-1.5">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-xs uppercase tracking-wide text-zinc-400">Corpo (template)</Label>
                                        {requisicaoExemplo && (
                                            <span className="text-[10px] text-emerald-400/80 font-mono flex items-center gap-1">
                                                <Eye size={11} /> Preview com {requisicaoExemplo.metodo} {requisicaoExemplo.id.substring(0, 12)}…
                                            </span>
                                        )}
                                    </div>
                                    <Textarea
                                        data-testid="input-corpo-regra"
                                        rows={8}
                                        value={form.corpo_template}
                                        onChange={(e) => setForm({ ...form, corpo_template: e.target.value })}
                                        onFocus={() => setCampoAtivo("corpo_template")}
                                        className="bg-zinc-900 border-zinc-800 font-mono text-xs" />
                                </div>

                                {/* PREVIEW */}
                                {requisicaoExemplo && (
                                    <div className="space-y-1.5">
                                        <Label className="text-xs uppercase tracking-wide text-emerald-400/80 flex items-center gap-1">
                                            <Eye size={12} /> Preview do corpo final {carregandoPreview && <span className="text-zinc-500">(atualizando…)</span>}
                                        </Label>
                                        <pre className="bg-black border border-emerald-500/20 rounded p-2 text-[11px] font-mono text-zinc-300 max-h-[200px] overflow-auto whitespace-pre-wrap break-all" data-testid="preview-corpo-regra">
                                            {preview || "(vazio)"}
                                        </pre>
                                    </div>
                                )}

                                <fieldset className="border border-zinc-800 rounded-md p-3 space-y-3">
                                    <legend className="text-[10px] uppercase tracking-wider text-zinc-500 px-2">Gatilho — quando esta regra dispara</legend>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="space-y-1.5">
                                            <Label className="text-xs uppercase tracking-wide text-zinc-400">Tipo</Label>
                                            <Select value={form.gatilho_tipo} onValueChange={(v) => setForm({ ...form, gatilho_tipo: v })}>
                                                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="select-gatilho-regra">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent className="bg-zinc-900 border-zinc-800">
                                                    <SelectItem value="manual">Manual (botão Executar)</SelectItem>
                                                    <SelectItem value="captura">Na captura de requisição</SelectItem>
                                                    <SelectItem value="intervalo">A cada X milissegundos</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {form.gatilho_tipo === "intervalo" && (
                                            <div className="space-y-1.5">
                                                <Label className="text-xs uppercase tracking-wide text-zinc-400">Intervalo (ms)</Label>
                                                <Input
                                                    data-testid="input-gatilho-intervalo-regra"
                                                    type="number" min="5000" step="1000"
                                                    value={form.gatilho_intervalo_ms}
                                                    onChange={(e) => setForm({ ...form, gatilho_intervalo_ms: e.target.value })}
                                                    className="bg-zinc-900 border-zinc-800 font-mono text-xs" />
                                            </div>
                                        )}
                                    </div>
                                    {form.gatilho_tipo === "captura" && (
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_180px] gap-3">
                                                <div className="space-y-1.5">
                                                    <Label className="text-xs uppercase tracking-wide text-zinc-400">URL contém / casa</Label>
                                                    <Input
                                                        data-testid="input-gatilho-url-regra"
                                                        value={form.gatilho_url_contem}
                                                        onChange={(e) => setForm({ ...form, gatilho_url_contem: e.target.value })}
                                                        placeholder="*/api/graphql*"
                                                        className="bg-zinc-900 border-zinc-800 font-mono text-xs" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <Label className="text-xs uppercase tracking-wide text-zinc-400">Tipo de match</Label>
                                                    <Select value={form.gatilho_url_padrao} onValueChange={(v) => setForm({ ...form, gatilho_url_padrao: v })}>
                                                        <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="select-padrao-url">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-zinc-900 border-zinc-800">
                                                            {padroes.map((p) => (
                                                                <SelectItem key={p.valor} value={p.valor}>{p.rotulo}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                                <div className="space-y-1.5">
                                                    <Label className="text-xs uppercase tracking-wide text-zinc-400">Método HTTP</Label>
                                                    <Input
                                                        data-testid="input-gatilho-metodo-regra"
                                                        value={form.gatilho_metodo}
                                                        onChange={(e) => setForm({ ...form, gatilho_metodo: e.target.value })}
                                                        placeholder="qualquer"
                                                        className="bg-zinc-900 border-zinc-800 font-mono text-xs" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <Label className="text-xs uppercase tracking-wide text-zinc-400">Tipo recurso</Label>
                                                    <Input
                                                        data-testid="input-gatilho-tipo-recurso"
                                                        value={form.gatilho_tipo_recurso}
                                                        onChange={(e) => setForm({ ...form, gatilho_tipo_recurso: e.target.value })}
                                                        placeholder="XHR/Fetch"
                                                        className="bg-zinc-900 border-zinc-800 font-mono text-xs" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <Label className="text-xs uppercase tracking-wide text-zinc-400">Status min</Label>
                                                    <Input
                                                        data-testid="input-status-min"
                                                        type="number" min="0" max="599"
                                                        value={form.gatilho_status_min}
                                                        onChange={(e) => setForm({ ...form, gatilho_status_min: e.target.value })}
                                                        placeholder="0 = ignorar"
                                                        className="bg-zinc-900 border-zinc-800 font-mono text-xs" />
                                                </div>
                                                <div className="space-y-1.5">
                                                    <Label className="text-xs uppercase tracking-wide text-zinc-400">Status max</Label>
                                                    <Input
                                                        data-testid="input-status-max"
                                                        type="number" min="0" max="599"
                                                        value={form.gatilho_status_max}
                                                        onChange={(e) => setForm({ ...form, gatilho_status_max: e.target.value })}
                                                        placeholder="0 = ignorar"
                                                        className="bg-zinc-900 border-zinc-800 font-mono text-xs" />
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-zinc-500 leading-relaxed">
                                                <strong className="text-zinc-300">Dicas:</strong> Wildcard <code>*/api/graphql*</code> casa
                                                qualquer URL com <code>/api/graphql</code>. Regex permite padrões avançados.
                                                Deixe status min/max em 0 para aceitar qualquer código.
                                            </p>
                                        </div>
                                    )}
                                </fieldset>

                                <div className="flex items-center gap-2">
                                    <Switch checked={form.ativa} onCheckedChange={(v) => setForm({ ...form, ativa: v })}
                                        data-testid="switch-ativa-regra" />
                                    <Label className="text-xs text-zinc-400">Ativa</Label>
                                </div>

                                <DialogFooter className="gap-2 pt-2 border-t border-zinc-800">
                                    <Button type="button" variant="outline"
                                        className="bg-transparent border-zinc-800 hover:bg-zinc-900"
                                        onClick={() => { setDialogAberto(false); }}>Cancelar</Button>
                                    <Button type="submit" disabled={salvando}
                                        data-testid="confirmar-criar-regra"
                                        className="bg-white text-zinc-950 hover:bg-zinc-200">
                                        {salvando ? "Salvando…" : (editandoId ? "Atualizar regra" : "Salvar regra")}
                                    </Button>
                                </DialogFooter>
                            </form>

                            {/* COLUNA DIREITA: VARIÁVEIS DISPONÍVEIS */}
                            <aside className="hidden lg:block">
                                <div className="sticky top-0 max-h-[80vh] overflow-y-auto pr-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <MagicWand size={14} className="text-emerald-400" />
                                        <span className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Variáveis disponíveis</span>
                                    </div>
                                    <p className="text-[10px] text-zinc-500 mb-3">
                                        Clique numa variável para inserir no campo focado:
                                        <span className="block mt-1 text-zinc-400">→ {{
                                            corpo_template: "Corpo",
                                            headers_text: "Headers",
                                            url_destino: "URL",
                                        }[campoAtivo] || "Corpo"}</span>
                                    </p>
                                    <div className="space-y-3" data-testid="painel-variaveis">
                                        {grupos.map((g) => (
                                            <div key={g.titulo} className="bg-zinc-900 border border-zinc-800 rounded p-2">
                                                <div className="text-[10px] uppercase tracking-wider text-emerald-400/70 mb-1.5 font-mono">{g.titulo}</div>
                                                <div className="space-y-1">
                                                    {(g.variaveis || []).map((v) => (
                                                        <button
                                                            key={v.chave}
                                                            type="button"
                                                            onClick={() => inserirVariavel(v.chave)}
                                                            title={v.descricao}
                                                            className="w-full text-left text-[11px] font-mono text-zinc-300 hover:bg-emerald-500/10 hover:text-emerald-300 px-1.5 py-0.5 rounded transition-colors block"
                                                        >
                                                            {v.chave}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </aside>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {regras.length === 0 ? (
                <div className="border border-dashed border-zinc-800 rounded-md p-10 text-center text-zinc-500 text-sm">
                    Nenhuma regra configurada.
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="lista-regras">
                    {regras.map((r) => (
                        <div key={r.id} className="bg-zinc-900 border border-zinc-800 rounded-md p-3" data-testid={`regra-${r.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-medium text-sm truncate">{r.nome || "(sem nome)"}</span>
                                        <Badge variant="outline" className="text-[9px] font-mono uppercase border-zinc-700">{r.metodo}</Badge>
                                        <Badge variant="outline" className="text-[9px] font-mono uppercase border-zinc-700">{r.gatilho_tipo}</Badge>
                                        {r.ativa
                                            ? <Badge className="text-[9px] bg-emerald-500/10 text-emerald-400 border-emerald-500/30">ATIVA</Badge>
                                            : <Badge className="text-[9px] bg-zinc-700/30 text-zinc-400">PAUSADA</Badge>}
                                    </div>
                                    <p className="text-[11px] text-zinc-500 mt-1 font-mono truncate">{r.url_destino}</p>
                                    {r.gatilho_url_contem && (
                                        <p className="text-[10px] text-zinc-600 mt-0.5 font-mono">
                                            {r.gatilho_url_padrao || "contem"}: <span className="text-emerald-400/70">{r.gatilho_url_contem}</span>
                                        </p>
                                    )}
                                </div>
                            </div>
                            {r.ultimo_status_codigo > 0 && (
                                <div className="mt-2 space-y-1.5 text-[11px] font-mono">
                                    <div className="flex items-center gap-2">
                                        {r.ultimo_status_codigo < 400
                                            ? <CheckCircle size={12} className="text-emerald-400" />
                                            : <XCircle size={12} className="text-red-400" />}
                                        <span className="text-zinc-400">Última exec: status {r.ultimo_status_codigo}</span>
                                    </div>
                                    {r.ultimo_status_codigo >= 400 && r.ultima_resposta_corpo && (
                                        <details className="bg-red-500/5 border border-red-500/20 rounded p-1.5" data-testid={`erro-resposta-${r.id}`}>
                                            <summary className="text-[10px] text-red-300 cursor-pointer">Resposta do destino (clique)</summary>
                                            <pre className="mt-1 text-[10px] text-red-200 whitespace-pre-wrap break-all max-h-[120px] overflow-auto">{r.ultima_resposta_corpo}</pre>
                                        </details>
                                    )}
                                    {r.ultimo_corpo_enviado && (
                                        <details className="bg-zinc-800/40 border border-zinc-700/40 rounded p-1.5" data-testid={`payload-enviado-${r.id}`}>
                                            <summary className="text-[10px] text-zinc-400 cursor-pointer">Payload enviado (clique)</summary>
                                            {r.ultima_url_enviada && (
                                                <p className="mt-1 text-[10px] text-emerald-400/80 break-all">→ {r.ultima_url_enviada}</p>
                                            )}
                                            <pre className="mt-1 text-[10px] text-zinc-300 whitespace-pre-wrap break-all max-h-[160px] overflow-auto">{r.ultimo_corpo_enviado}</pre>
                                        </details>
                                    )}
                                </div>
                            )}
                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-zinc-800/60">
                                <Button size="sm" variant="outline" data-testid={`btn-exec-${r.id}`}
                                    className="bg-transparent border-zinc-800 hover:bg-zinc-800 text-xs"
                                    onClick={() => executar(r)}>
                                    <Play size={12} className="mr-1" /> Executar
                                </Button>
                                <Button size="sm" variant="outline" data-testid={`btn-editar-${r.id}`}
                                    className="bg-transparent border-zinc-800 hover:bg-zinc-800 text-xs"
                                    onClick={() => abrirParaEditar(r)}>
                                    <PencilSimple size={12} className="mr-1" /> Editar
                                </Button>
                                <Button size="sm" variant="outline" data-testid={`btn-toggle-${r.id}`}
                                    className="bg-transparent border-zinc-800 hover:bg-zinc-800 text-xs"
                                    onClick={() => alternarAtiva(r)}>
                                    {r.ativa ? "Pausar" : "Ativar"}
                                </Button>
                                <Button size="sm" variant="outline" data-testid={`btn-del-${r.id}`}
                                    className="bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-400 ml-auto"
                                    onClick={() => excluir(r)}>
                                    <Trash size={12} />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {eventosRegra && eventosRegra.length > 0 && (
                <div className="bg-zinc-900 border border-zinc-800 rounded-md mt-4">
                    <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500 font-mono border-b border-zinc-800">
                        Execuções recentes (ao vivo)
                    </div>
                    <ScrollArea className="max-h-[320px]">
                        {eventosRegra.map((ev, i) => (
                            <div key={i} className="px-3 py-2 border-b border-zinc-800/50 text-xs font-mono">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={ev.status_codigo >= 400 ? "text-red-400" : "text-emerald-400"}>
                                        {ev.status_codigo || "ERR"}
                                    </span>
                                    <span className="text-zinc-500 text-[10px]">{new Date(ev.executado_em).toLocaleTimeString("pt-BR")}</span>
                                    {ev.url_enviada && <span className="text-zinc-400 text-[10px] truncate flex-1">→ {ev.url_enviada}</span>}
                                    {ev.erro && <span className="text-red-400 truncate">{ev.erro}</span>}
                                </div>
                                {ev.corpo_enviado && (
                                    <details className="mt-1.5 bg-zinc-800/40 border border-zinc-700/40 rounded p-1.5">
                                        <summary className="text-[10px] text-zinc-400 cursor-pointer">Payload enviado</summary>
                                        <pre className="mt-1 text-[10px] text-zinc-300 whitespace-pre-wrap break-all max-h-[140px] overflow-auto">{ev.corpo_enviado}</pre>
                                    </details>
                                )}
                                {ev.corpo && (
                                    <details className={`mt-1 ${ev.status_codigo >= 400 ? "bg-red-500/5 border-red-500/20" : "bg-zinc-800/40 border-zinc-700/40"} border rounded p-1.5`}>
                                        <summary className={`text-[10px] cursor-pointer ${ev.status_codigo >= 400 ? "text-red-300" : "text-zinc-400"}`}>Resposta do destino</summary>
                                        <pre className={`mt-1 text-[10px] whitespace-pre-wrap break-all max-h-[140px] overflow-auto ${ev.status_codigo >= 400 ? "text-red-200" : "text-zinc-300"}`}>{ev.corpo}</pre>
                                    </details>
                                )}
                            </div>
                        ))}
                    </ScrollArea>
                </div>
            )}
        </div>
    );
});

// Tenta criar um padrão wildcard útil a partir de uma URL específica.
// Ex: "https://x.com/api/graphql/abc?d=1" → "*/api/graphql*"
function extrairPadraoURL(urlCompleta) {
    if (!urlCompleta) return "";
    try {
        const u = new URL(urlCompleta);
        // Pega os 2 primeiros segmentos do path como "núcleo" do padrão
        const segs = u.pathname.split("/").filter(Boolean);
        if (segs.length === 0) return u.host;
        if (segs.length === 1) return `*/${segs[0]}*`;
        return `*/${segs[0]}/${segs[1]}*`;
    } catch {
        return urlCompleta;
    }
}

export default AbaRegras;
